import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { 
    Play,
    RotateCcw,
    Trophy,
    Home,
    ChevronLeft,
} from 'lucide-react';

const GRAVITY = 0.08;
const FLAP_POWER = -2.5;
const PIPE_SPEED = 1.2;
const PIPE_GAP = 40;
const PIPE_WIDTH = 14;
const BIRD_SIZE = 5;
const PIPE_SPAWN_INTERVAL = 130;

const FlappyBird: React.FC = () => {
    const [gameState, setGameState] = useState<'menu' | 'playing' | 'gameover'>('menu');
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(() => {
        const saved = localStorage.getItem('flappyHighScore');
        return saved ? parseInt(saved) : 0;
    });
    const [displayY, setDisplayY] = useState(50);
    const [rotation, setRotation] = useState(0);

    const birdYRef = useRef(50);
    const velocityRef = useRef(0);
    const pipesRef = useRef<{ x: number; gapY: number; passed: boolean }[]>([]);
    const gameStateRef = useRef(gameState);
    const frameRef = useRef<number>();
    const lastPipeSpawnRef = useRef(0);

    useEffect(() => {
        gameStateRef.current = gameState;
    }, [gameState]);

    const resetGame = useCallback(() => {
        birdYRef.current = 50;
        velocityRef.current = 0;
        pipesRef.current = [];
        lastPipeSpawnRef.current = 0;
        setScore(0);
        setDisplayY(50);
        setRotation(0);
    }, []);

    const startGame = useCallback(() => {
        resetGame();
        setGameState('playing');
    }, [resetGame]);

    const flap = useCallback(() => {
        if (gameStateRef.current === 'playing') {
            velocityRef.current = FLAP_POWER;
        } else {
            startGame();
        }
    }, [startGame]);

    // 游戏主循环
    useEffect(() => {
        if (gameState !== 'playing') {
            if (frameRef.current) {
                cancelAnimationFrame(frameRef.current);
            }
            return;
        }

        let lastTime = performance.now();

        const gameLoop = (currentTime: number) => {
            const deltaTime = (currentTime - lastTime) / 16.67;
            lastTime = currentTime;

            // 应用重力
            velocityRef.current += GRAVITY * deltaTime;
            
            // 限制最大下落速度
            if (velocityRef.current > 3) {
                velocityRef.current = 3;
            }

            // 更新位置
            birdYRef.current += velocityRef.current * deltaTime;

            // 边界检测
            if (birdYRef.current <= BIRD_SIZE) {
                birdYRef.current = BIRD_SIZE;
                velocityRef.current = 0;
                setGameState('gameover');
                return;
            }
            if (birdYRef.current >= 92 - BIRD_SIZE) {
                birdYRef.current = 92 - BIRD_SIZE;
                setGameState('gameover');
                return;
            }

            // 更新显示
            setDisplayY(birdYRef.current);
            setRotation(Math.max(-30, Math.min(velocityRef.current * 5, 70)));

            // 生成管道
            lastPipeSpawnRef.current += deltaTime;
            if (lastPipeSpawnRef.current > PIPE_SPAWN_INTERVAL / (16.67 * (PIPE_SPEED / 2))) {
                lastPipeSpawnRef.current = 0;
                const gapY = 25 + Math.random() * 50;
                pipesRef.current.push({ x: 105, gapY, passed: false });
            }

            // 更新管道位置
            pipesRef.current = pipesRef.current
                .map(pipe => ({ ...pipe, x: pipe.x - PIPE_SPEED * deltaTime }))
                .filter(pipe => pipe.x > -PIPE_WIDTH);

            // 碰撞检测
            const birdLeft = 18;
            const birdRight = birdLeft + BIRD_SIZE;
            const birdTop = birdYRef.current;
            const birdBottom = birdYRef.current + BIRD_SIZE;

            for (const pipe of pipesRef.current) {
                const pipeLeft = pipe.x;
                const pipeRight = pipe.x + PIPE_WIDTH;
                const gapTop = pipe.gapY - PIPE_GAP / 2;
                const gapBottom = pipe.gapY + PIPE_GAP / 2;

                if (birdRight > pipeLeft && birdLeft < pipeRight) {
                    if (birdTop < gapTop || birdBottom > gapBottom) {
                        setGameState('gameover');
                        return;
                    }
                }

                if (!pipe.passed && pipe.x + PIPE_WIDTH < birdLeft) {
                    pipe.passed = true;
                    setScore(s => {
                        const newScore = s + 1;
                        if (newScore > highScore) {
                            setHighScore(newScore);
                            localStorage.setItem('flappyHighScore', newScore.toString());
                        }
                        return newScore;
                    });
                }
            }

            frameRef.current = requestAnimationFrame(gameLoop);
        };

        frameRef.current = requestAnimationFrame(gameLoop);

        return () => {
            if (frameRef.current) {
                cancelAnimationFrame(frameRef.current);
            }
        };
    }, [gameState, highScore]);

    // 键盘控制
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') {
                e.preventDefault();
                flap();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [flap]);

    const renderPipes = () => (
        <>
            {pipesRef.current.map((pipe, index) => (
                <div key={index}>
                    <div 
                        className="absolute bg-gradient-to-r from-green-600 to-green-500 border-l-2 border-r-2 border-green-700"
                        style={{
                            left: `${pipe.x}%`,
                            top: 0,
                            width: `${PIPE_WIDTH}%`,
                            height: `${pipe.gapY - PIPE_GAP / 2}%`,
                        }}
                    >
                        <div className="absolute bottom-0 left-0 right-0 h-3 bg-green-800" />
                    </div>
                    <div 
                        className="absolute bg-gradient-to-r from-green-600 to-green-500 border-l-2 border-r-2 border-green-700"
                        style={{
                            left: `${pipe.x}%`,
                            top: `${pipe.gapY + PIPE_GAP / 2}%`,
                            width: `${PIPE_WIDTH}%`,
                            height: `${92 - (pipe.gapY + PIPE_GAP / 2)}%`,
                        }}
                    >
                        <div className="absolute top-0 left-0 right-0 h-3 bg-green-800" />
                    </div>
                </div>
            ))}
        </>
    );

    // 菜单界面
    if (gameState === 'menu') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-sky-400 via-sky-300 to-green-400 flex flex-col items-center justify-center p-4">
                <SEO title="像素鸟" description="经典像素鸟小游戏，点击或按空格键让小鸟飞翔！" />
                
                <div className="text-center mb-8">
                    <h1 className="text-5xl md:text-6xl font-bold text-white mb-2 drop-shadow-lg" style={{ 
                        textShadow: '3px 3px 0 #059669, 6px 6px 0 rgba(0,0,0,0.2)'
                    }}>
                        像素鸟
                    </h1>
                    <p className="text-white/80 text-lg">经典 Flappy Bird 小游戏</p>
                </div>

                <div 
                    className="w-20 h-20 mb-8 cursor-pointer"
                    onClick={startGame}
                >
                    <svg viewBox="0 0 24 24" className="w-full h-full drop-shadow-xl animate-bounce" style={{ animationDuration: '1.5s' }}>
                        <rect x="6" y="8" width="12" height="10" fill="#FFD93D" />
                        <rect x="14" y="10" width="4" height="4" fill="white" />
                        <rect x="15" y="11" width="2" height="2" fill="black" />
                        <rect x="18" y="12" width="4" height="3" fill="#FF6B35" />
                        <rect x="8" y="6" width="6" height="4" fill="#FFD93D" />
                        <rect x="10" y="18" width="2" height="3" fill="#FF6B35" />
                        <rect x="14" y="18" width="2" height="3" fill="#FF6B35" />
                    </svg>
                </div>

                <button
                    onClick={startGame}
                    className="mb-6 px-8 py-4 bg-white text-green-600 rounded-xl font-bold text-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all flex items-center gap-3"
                >
                    <Play className="w-6 h-6" />
                    开始游戏
                </button>

                {highScore > 0 && (
                    <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-xl mb-6">
                        <div className="flex items-center gap-2 text-white">
                            <Trophy className="w-5 h-5" />
                            <span className="font-bold">{highScore}</span>
                            <span className="text-white/80">最高分</span>
                        </div>
                    </div>
                )}

                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center text-white max-w-xs">
                    <p className="font-semibold mb-2">🎮 游戏说明</p>
                    <p className="text-sm text-white/90">点击屏幕或按<span className="font-bold">空格键</span>让小鸟飞翔</p>
                    <p className="text-sm text-white/90 mt-1">穿过管道得 1 分</p>
                </div>

                <Link to="/" className="flex items-center gap-2 mt-6 text-white/70 hover:text-white transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                    返回首页
                </Link>
            </div>
        );
    }

    // 游戏结束界面
    if (gameState === 'gameover') {
        const isNewHighScore = score >= highScore && score > 0;
        
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex flex-col items-center justify-center p-4">
                <SEO title="游戏结束" description="游戏结束，来看看你的得分吧！" />
                
                <div className="text-center mb-8">
                    <h2 className="text-4xl font-bold text-white mb-2">游戏结束</h2>
                    {isNewHighScore && (
                        <div className="inline-flex items-center gap-2 bg-yellow-500 text-yellow-900 px-4 py-2 rounded-full font-bold mb-2 animate-pulse">
                            <Trophy className="w-5 h-5" />
                            新纪录！
                        </div>
                    )}
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8 text-center">
                    <p className="text-white/70 mb-2">本次得分</p>
                    <p className="text-6xl font-bold text-white mb-4">{score}</p>
                    {highScore > 0 && (
                        <div className="flex items-center justify-center gap-2 text-white/60">
                            <Trophy className="w-4 h-4" />
                            <span>最高分: {highScore}</span>
                        </div>
                    )}
                </div>

                <div className="flex gap-4">
                    <button
                        onClick={startGame}
                        className="px-6 py-3 bg-green-500 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all flex items-center gap-2"
                    >
                        <RotateCcw className="w-5 h-5" />
                        再来一局
                    </button>
                    <Link
                        to="/game"
                        className="px-6 py-3 bg-white/20 text-white rounded-xl font-bold text-lg shadow-lg hover:bg-white/30 transition-all flex items-center gap-2"
                    >
                        <Home className="w-5 h-5" />
                        游戏列表
                    </Link>
                </div>
            </div>
        );
    }

    // 游戏进行中
    return (
        <div className="min-h-screen flex flex-col">
            <SEO title="像素鸟 - 游戏中" description="像素鸟游戏中，加油！" />
            
            <div className="bg-black/50 backdrop-blur-sm px-4 py-2 flex items-center justify-between">
                <Link
                    to="/game"
                    className="text-white/80 hover:text-white flex items-center gap-1 text-sm"
                >
                    <ChevronLeft className="w-4 h-4" />
                    返回
                </Link>
                <div className="flex items-center gap-4">
                    <div className="text-white/60 text-sm">
                        <Trophy className="w-4 h-4 inline mr-1" />
                        {highScore}
                    </div>
                </div>
            </div>

            <div 
                className="flex-1 relative bg-gradient-to-b from-sky-300 via-sky-200 to-green-300 overflow-hidden cursor-pointer"
                onClick={flap}
                style={{ height: 'calc(100vh - 52px)' }}
            >
                {/* 云朵装饰 */}
                <div className="absolute top-[8%] left-[5%] w-16 h-8 bg-white/70 rounded-full" />
                <div className="absolute top-[12%] left-[40%] w-12 h-6 bg-white/60 rounded-full" />
                <div className="absolute top-[5%] left-[70%] w-20 h-10 bg-white/80 rounded-full" />
                <div className="absolute top-[18%] left-[25%] w-10 h-5 bg-white/50 rounded-full" />
                
                {/* 地面 */}
                <div className="absolute bottom-0 left-0 right-0 h-[8%] bg-gradient-to-t from-green-600 to-green-500">
                    <div className="absolute top-0 left-0 right-0 h-2 bg-green-700" />
                </div>

                {/* 管道 */}
                {renderPipes()}

                {/* 小鸟 */}
                <div 
                    className="absolute transition-transform duration-75"
                    style={{
                        left: '18%',
                        top: `${displayY}%`,
                        width: `${BIRD_SIZE}%`,
                        height: `${BIRD_SIZE}%`,
                        transform: `rotate(${rotation}deg)`,
                    }}
                >
                    <svg viewBox="0 0 24 24" className="w-full h-full drop-shadow-lg">
                        <rect x="6" y="8" width="12" height="10" fill="#FFD93D" />
                        <rect x="14" y="10" width="4" height="4" fill="white" />
                        <rect x="15" y="11" width="2" height="2" fill="black" />
                        <rect x="18" y="12" width="4" height="3" fill="#FF6B35" />
                        <rect x="8" y="6" width="6" height="4" fill="#FFD93D" />
                        <rect x="10" y="18" width="2" height="3" fill="#FF6B35" />
                        <rect x="14" y="18" width="2" height="3" fill="#FF6B35" />
                    </svg>
                </div>

                {/* 分数 */}
                <div className="absolute top-4 left-0 right-0 text-center">
                    <div className="inline-block bg-black/30 backdrop-blur-sm px-6 py-2 rounded-xl">
                        <span className="text-white text-3xl font-bold drop-shadow-lg">{score}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FlappyBird;
